// src/services/dashboardApi.ts
import axios from 'axios';
import { setupAxiosInterceptors } from '../utils/axiosSetup';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000',
  timeout: 30000,
  withCredentials: true,
});

// Set up standard interceptors (includes 401 handling)
setupAxiosInterceptors(apiClient);

// ============================================================================
// TypeScript Interfaces
// ============================================================================

export interface Alert {
  type: 'OVERDUE_TASK' | 'UPCOMING_DEADLINE' | 'BLOCKED_TASK' | 'PROJECT_ISSUE' | 
        'AIRPORT_OVERDUE' | 'PROJECT_AT_RISK' | 'RESOURCE_CONFLICT' | 'DEADLINE_APPROACHING' | 
        'SYSTEM_HEALTH' | 'OVERDUE_PROJECT' | 'USER_ISSUE' | 'DATA_INTEGRITY';
  severity: 'CRITICAL' | 'WARNING' | 'INFO';
  message: string;
  entityId: number;
  entityType: 'task' | 'project' | 'user' | 'system';
  actionUrl: string;
}

export interface AlertsResponse {
  alerts: Alert[];
}

export interface OrganizationStatsResponse {
  kpiScore: number;
  projectCount: number;
  taskCount: number;
  completedTaskCount: number;
}

export interface TaskSummaryResponse {
  totalTasks: number;
  pastDueCount: number;
  upcomingCount: number;
  inProgressCount: number;
  completeCount: number;
}

export interface ProjectStatusSummaryResponse {
  PLANNING: number;
  EXECUTION: number;
  COMPLETE: number;
  CANCELLED: number;
  total: number;
}

export interface UserKpiStatsResponse {
  totalTasks: number;
  completedTasks: number;
  completionRate: number;
  averageKpiScore: number;
  tasksOnTime: number;
  tasksBehind: number;
  activeProjects: number;
}

export interface AirportKpiStatsResponse {
  totalProjects: number;
  activeProjects: number;
  totalTasks: number;
  completionRate: number;
  averageKpiScore: number;
  projectsOnTrack: number;
  projectsAtRisk: number;
  totalUsers: number;
}

export interface SystemKpiStatsResponse {
  totalProjects: number;
  activeProjects: number;
  totalTasks: number;
  completionRate: number;
  averageKpiScore: number;
  activeUsers: number;
  systemHealthScore: number;
  totalAirports: number;
}

export interface SystemHealthResponse {
  database: {
    status: 'healthy' | 'unhealthy';
    lastCheck: string;
  };
  api: {
    status: 'healthy' | 'unhealthy';
    lastCheck: string;
  };
  site: {
    status: 'healthy' | 'unhealthy';
    lastCheck: string;
  };
  lastSuccessfulLogDate: string | null;
  activeUsers: number;
  systemMetrics: {
    totalProjects: number;
    totalTasks: number;
    totalActions: number;
  };
}

export interface ProjectListItem {
  projectId: number;
  name: string;
  status: string;
  lastUpdated: string;
}

export interface ProjectsQuickAccessResponse {
  projects: ProjectListItem[];
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface TaskViewActionItem {
  actionId: number;
  name: string;
  status: string;
  actionType: string;
  assignedToCurrentUser?: boolean;
  assignedUsers?: { userId: number; displayName: string; email: string }[];
  documentTitle?: string;
  documentDescription?: string;
}

export interface TaskViewItem {
  taskId: number;
  name: string;
  projectId: number;
  projectName: string;
  phaseId: number;
  phaseName: string;
  contractedEndDate: string;
  status: string;
  daysOverdue?: number;
  daysUntilDeadline?: number;
  isPastDue: boolean;
  actions?: TaskViewActionItem[];
}

export interface TasksViewResponse {
  tasks: TaskViewItem[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface DashboardApiError {
  error: boolean;
  message: string;
  code?: 'AUTH_REQUIRED' | 'VALIDATION_ERROR' | 'NETWORK_ERROR' | 'NOT_FOUND';
}

// ============================================================================
// Alerts API Functions
// ============================================================================

/**
 * Get user alerts
 * @param userId - User ID
 * @param options - Optional: { pastDueOnly: true } to return only past-due action/task alerts
 */
export async function getUserAlerts(userId: number, options?: { pastDueOnly?: boolean }): Promise<AlertsResponse> {
  try {
    const params = new URLSearchParams();
    if (options?.pastDueOnly === true) {
      params.append('pastDueOnly', 'true');
    }
    const query = params.toString();
    const response = await apiClient.get(`/api/users/${userId}/alerts${query ? '?' + query : ''}`);
    return response.data;
  } catch (error: any) {
    handleApiError(error, 'Failed to fetch user alerts');
    throw error;
  }
}

/**
 * Get airport alerts (Manager only)
 * @param airportId - Airport ID
 * @param options - Optional: { pastDueOnly: true } to return only past-due alerts
 */
export async function getAirportAlerts(airportId: number, options?: { pastDueOnly?: boolean }): Promise<AlertsResponse> {
  try {
    const params = new URLSearchParams();
    if (options?.pastDueOnly === true) {
      params.append('pastDueOnly', 'true');
    }
    const query = params.toString();
    const response = await apiClient.get(`/api/airports/${airportId}/alerts${query ? '?' + query : ''}`);
    return response.data;
  } catch (error: any) {
    handleApiError(error, 'Failed to fetch airport alerts');
    throw error;
  }
}

/**
 * Get system-wide past-due alerts (Exec Admin / System Admin)
 * Returns only overdue task/project alerts across all airports.
 */
export async function getSystemPastDueAlerts(): Promise<AlertsResponse> {
  try {
    const response = await apiClient.get('/api/admin/past-due-alerts');
    return response.data;
  } catch (error: any) {
    handleApiError(error, 'Failed to fetch past-due alerts');
    throw error;
  }
}

/**
 * Get system alerts (Admin only)
 */
export async function getSystemAlerts(): Promise<AlertsResponse> {
  try {
    const response = await apiClient.get('/api/admin/system-alerts');
    return response.data;
  } catch (error: any) {
    handleApiError(error, 'Failed to fetch system alerts');
    throw error;
  }
}

// ============================================================================
// Performance Metric API Functions
// ============================================================================

/**
 * Get user performance metric (KPI score)
 */
export async function getUserPerformanceMetric(userId: number): Promise<OrganizationStatsResponse> {
  try {
    const response = await apiClient.get(`/api/organization-stats/user/${userId}`);
    return response.data;
  } catch (error: any) {
    handleApiError(error, 'Failed to fetch user performance metric');
    throw error;
  }
}

/**
 * Get airport performance metric (Manager only)
 */
export async function getAirportPerformanceMetric(airportId: number): Promise<OrganizationStatsResponse> {
  try {
    const response = await apiClient.get(`/api/organization-stats/airport/${airportId}`);
    return response.data;
  } catch (error: any) {
    handleApiError(error, 'Failed to fetch airport performance metric');
    throw error;
  }
}

/**
 * Get system performance metric (Admin only)
 */
export async function getSystemPerformanceMetric(): Promise<OrganizationStatsResponse> {
  try {
    const response = await apiClient.get('/api/organization-stats/system');
    return response.data;
  } catch (error: any) {
    handleApiError(error, 'Failed to fetch system performance metric');
    throw error;
  }
}

// ============================================================================
// KPI Stats API Functions
// ============================================================================

/**
 * Get user KPI stats
 */
export async function getUserKpiStats(userId: number): Promise<UserKpiStatsResponse> {
  try {
    const response = await apiClient.get(`/api/organization-stats/user/${userId}/stats`);
    return response.data;
  } catch (error: any) {
    handleApiError(error, 'Failed to fetch user KPI stats');
    throw error;
  }
}

/**
 * Get airport KPI stats (Manager only)
 */
export async function getAirportKpiStats(airportId: number): Promise<AirportKpiStatsResponse> {
  try {
    const response = await apiClient.get(`/api/airports/${airportId}/stats`);
    return response.data;
  } catch (error: any) {
    handleApiError(error, 'Failed to fetch airport KPI stats');
    throw error;
  }
}

/**
 * Get system KPI stats (Admin only)
 */
export async function getSystemKpiStats(): Promise<SystemKpiStatsResponse> {
  try {
    const response = await apiClient.get('/api/admin/system-stats');
    return response.data;
  } catch (error: any) {
    handleApiError(error, 'Failed to fetch system KPI stats');
    throw error;
  }
}

// ============================================================================
// Task Summary API Functions
// ============================================================================

export interface TaskSummaryFilters {
  userId?: number;
  airportId?: number;
  // System-wide summary: no filters
}

/**
 * Get task summary with filters
 */
export async function getTaskSummary(filters: TaskSummaryFilters = {}): Promise<TaskSummaryResponse> {
  try {
    const params = new URLSearchParams();
    
    if (filters.userId !== undefined) {
      params.append('userId', filters.userId.toString());
    }
    if (filters.airportId !== undefined) {
      params.append('airportId', filters.airportId.toString());
    }

    const url = `/api/tasks/summary${params.toString() ? '?' + params.toString() : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  } catch (error: any) {
    handleApiError(error, 'Failed to fetch task summary');
    throw error;
  }
}

// ============================================================================
// Tasks View API Functions
// ============================================================================

export interface TasksViewFilters {
  userId?: number;
  airportId?: number;
  page?: number;
  pageSize?: number;
  status?: string;
  pastDueOnly?: boolean;
  myInProgressActionsOnly?: boolean;
  // System-wide view: no userId or airportId
}

/**
 * Get tasks view with filters
 */
export async function getTasksView(filters: TasksViewFilters = {}): Promise<TasksViewResponse> {
  try {
    const params = new URLSearchParams();
    
    if (filters.userId !== undefined) {
      params.append('userId', filters.userId.toString());
    }
    if (filters.airportId !== undefined) {
      params.append('airportId', filters.airportId.toString());
    }
    if (filters.page !== undefined) {
      params.append('page', filters.page.toString());
    }
    if (filters.pageSize !== undefined) {
      params.append('pageSize', filters.pageSize.toString());
    }
    if (filters.status !== undefined) {
      params.append('status', filters.status);
    }
    if (filters.pastDueOnly !== undefined) {
      params.append('pastDueOnly', filters.pastDueOnly.toString());
    }
    if (filters.myInProgressActionsOnly === true) {
      params.append('myInProgressActionsOnly', 'true');
    }

    const url = `/api/tasks/cross-project${params.toString() ? '?' + params.toString() : ''}`;
    const response = await apiClient.get(url);
    
    // Transform the response to match TasksViewResponse format
    return {
      tasks: response.data.tasks || [],
      pagination: response.data.pagination || {
        page: 1,
        pageSize: 25,
        total: 0,
        totalPages: 0,
      },
    };
  } catch (error: any) {
    handleApiError(error, 'Failed to fetch tasks view');
    throw error;
  }
}

// ============================================================================
// Projects Quick Access API Functions
// ============================================================================

export interface ProjectsQuickAccessFilters {
  userId?: number;
  airportId?: number;
  page?: number;
  pageSize?: number;
  inProgressAssignmentsOnly?: boolean;
  // System-wide view: no userId or airportId
}

/**
 * Get projects quick access list
 */
export async function getProjectsQuickAccess(filters: ProjectsQuickAccessFilters = {}): Promise<ProjectsQuickAccessResponse> {
  try {
    const params = new URLSearchParams();
    
    if (filters.page !== undefined) {
      params.append('page', filters.page.toString());
    }
    if (filters.pageSize !== undefined) {
      params.append('pageSize', filters.pageSize.toString());
    }
    if (filters.inProgressAssignmentsOnly === true) {
      params.append('inProgressAssignmentsOnly', 'true');
    }

    let url: string;
    // When filtering by in-progress assignments only, use /api/projects (backend applies filter for current user)
    if (filters.inProgressAssignmentsOnly === true) {
      if (filters.userId !== undefined) {
        params.append('userId', filters.userId.toString());
      }
      url = `/api/projects${params.toString() ? '?' + params.toString() : ''}`;
    } else if (filters.airportId !== undefined) {
      // Use airport-specific endpoint if airportId is provided (similar to getProjectStatusSummary)
      url = `/api/airports/${filters.airportId}/projects${params.toString() ? '?' + params.toString() : ''}`;
    } else if (filters.userId !== undefined) {
      // For user-specific projects, use /api/projects with userId filter
      params.append('userId', filters.userId.toString());
      url = `/api/projects${params.toString() ? '?' + params.toString() : ''}`;
    } else {
      // System-wide: use /api/projects
      url = `/api/projects${params.toString() ? '?' + params.toString() : ''}`;
    }

    const response = await apiClient.get(url);

    // Transform to match ProjectsQuickAccessResponse format
    // Handle both /api/projects and /api/airports/{id}/projects response formats
    const projectsArray = response.data.projects || [];
    const transformed = {
      projects: projectsArray.map((p: any) => ({
        projectId: p.projectId,
        name: p.name,
        status: p.status,
        lastUpdated: p.lastUpdated || p.createdAt,
      })),
      pagination: response.data.pagination || {
        page: filters.page || 1,
        pageSize: filters.pageSize || 10,
        total: projectsArray.length,
        totalPages: Math.ceil((projectsArray.length) / (filters.pageSize || 10)),
      },
    };
    return transformed;
  } catch (error: any) {
    handleApiError(error, 'Failed to fetch projects quick access');
    throw error;
  }
}

// ============================================================================
// Project Status Summary API Functions
// ============================================================================

export interface ProjectStatusSummaryFilters {
  airportId?: number;
  // System-wide summary: no airportId
}

/**
 * Get project status summary
 */
export async function getProjectStatusSummary(filters: ProjectStatusSummaryFilters = {}): Promise<ProjectStatusSummaryResponse> {
  try {
    const params = new URLSearchParams();
    params.append('groupBy', 'status');
    
    if (filters.airportId !== undefined) {
      // Use airport-specific endpoint
      const response = await apiClient.get(`/api/airports/${filters.airportId}/projects?${params.toString()}`);
      return response.data;
    } else {
      // Use system-wide endpoint
      const response = await apiClient.get(`/api/projects?${params.toString()}`);
      return response.data;
    }
  } catch (error: any) {
    handleApiError(error, 'Failed to fetch project status summary');
    throw error;
  }
}

// ============================================================================
// System Health API Functions
// ============================================================================

/**
 * Get system health (Admin only)
 */
export async function getSystemHealth(): Promise<SystemHealthResponse> {
  try {
    const response = await apiClient.get('/api/admin/system-health');
    return response.data;
  } catch (error: any) {
    handleApiError(error, 'Failed to fetch system health');
    throw error;
  }
}

// ============================================================================
// Error Handling Helper
// ============================================================================

function handleApiError(error: any, defaultMessage: string): void {
  console.error(`${defaultMessage}:`, {
    status: error.response?.status,
    statusText: error.response?.statusText,
    data: error.response?.data,
    message: error.message,
    url: error.config?.url,
  });

  if (error.response?.status === 401) {
    const apiError: DashboardApiError = {
      error: true,
      message: 'Authentication required',
      code: 'AUTH_REQUIRED',
    };
    throw apiError;
  }

  if (error.response?.status === 400) {
    const apiError: DashboardApiError = {
      error: true,
      message: error.response.data?.message || 'Validation error',
      code: 'VALIDATION_ERROR',
    };
    throw apiError;
  }

  if (error.response?.status === 404) {
    const apiError: DashboardApiError = {
      error: true,
      message: error.response.data?.message || 'Resource not found',
      code: 'NOT_FOUND',
    };
    throw apiError;
  }

  if (error.response?.status === 500) {
    const errorMessage = error.response.data?.message || error.response.data?.error || 'Internal server error';
    const apiError: DashboardApiError = {
      error: true,
      message: `Server error: ${errorMessage}`,
      code: 'NETWORK_ERROR',
    };
    throw apiError;
  }

  const apiError: DashboardApiError = {
    error: true,
    message: error.message || defaultMessage,
    code: 'NETWORK_ERROR',
  };
  throw apiError;
}

